<template>
    <div class="kp-keypoint">
        <router-view></router-view>

        <div class="kp-page">
            <!-- 标题横幅 -->
            <div class="kp-banner">
                <h1>资源知识点</h1>
            </div>

            <!-- KPI -->
            <div class="kp-kpis">
                <div class="kp-kpi" style="--c:#4fc3ff;">
                    <div class="kp-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4fc3ff" stroke-width="2"><path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/><path d="M8 12h8M8 16h5"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">知识点总数</div>
                        <div class="value">{{ stat.total }}<small>条</small></div>
                        <div class="trend">文字知识点 {{ stat.textCount }} · 课程知识点 {{ stat.courseCount }}</div>
                    </div>
                </div>

                <div class="kp-kpi" style="--c:#35e0b0;">
                    <div class="kp-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#35e0b0" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">关联资源</div>
                        <div class="value">{{ stat.resCount }}<small>份</small></div>
                        <div class="trend">{{ stat.topResText }}</div>
                    </div>
                </div>

                <div class="kp-kpi" style="--c:#a78bfa;">
                    <div class="kp-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h8M8 17h5"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">页码定位</div>
                        <div class="value">{{ stat.pageLoc }}<small>条</small></div>
                        <div class="trend">文档类可跳页，最长跨 {{ stat.maxSpan }} 页</div>
                    </div>
                </div>

                <div class="kp-kpi" style="--c:#ffd24a;">
                    <div class="kp-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffd24a" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">视频定位</div>
                        <div class="value">{{ stat.videoLoc }}<small>条</small></div>
                        <div class="trend">原以秒存储，界面转 <i>mm:ss</i></div>
                    </div>
                </div>
            </div>

            <!-- 中段三栏 -->
            <div class="kp-grid">
                <div class="kp-card">
                    <div class="kp-title">知识点类型<span class="more">共 {{ stat.total }} 条</span></div>
                    <div ref="typeChart" class="kp-chart"></div>
                </div>

                <div class="kp-card">
                    <div class="kp-title" style="--accent:#35e0b0;">关联资源知识点数<span class="more">TOP {{ stat.resRank.length }} / 共 {{ stat.resCount }} 份</span></div>
                    <div class="kp-rank">
                        <div class="rank-row" v-for="(r, i) in stat.resRank" :key="r.id" :title="r.name">
                            <div class="rank-no" :class="'n' + (i + 1)">{{ i + 1 }}</div>
                            <div class="rank-name">{{ r.name }}</div>
                            <div class="rank-track">
                                <div class="rank-fill" :class="fillCls(i)" :style="{ width: r.pct + '%' }"></div>
                            </div>
                            <div class="rank-val"><b>{{ r.value }}</b> 条</div>
                        </div>
                        <div v-if="!stat.resRank.length" class="kp-empty">暂无数据</div>
                    </div>
                </div>

                <div class="kp-card">
                    <div class="kp-title" style="--accent:#ffd24a;">定位方式分布<span class="more">共 {{ stat.total }} 条</span></div>
                    <div class="loc-list">
                        <div class="loc-item" v-for="l in stat.locDist" :key="l.name">
                            <div class="loc-top">{{ l.name }}<em>{{ l.desc }}</em><b>{{ l.value }}</b></div>
                            <div class="loc-bar"><i :class="l.cls" :style="{ width: l.pct + '%' }"></i></div>
                        </div>
                        <div v-if="!stat.locDist.length" class="kp-empty">暂无数据</div>
                    </div>
                </div>
            </div>

            <!-- 明细表 -->
            <div class="kp-card">
                <div class="kp-title">知识点明细<span class="more">共 {{ total }} 条</span></div>

                <div class="kp-toolbar">
                    <div class="search-box">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f77ab" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
                        <input type="text" v-model="query.keyPoint" placeholder="知识点名称" @keyup.enter="doSearch" />
                    </div>
                    <select v-model="query.resourceId">
                        <option value="">全部资源</option>
                        <option v-for="r in resOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
                    </select>
                    <select v-model="query.keyPointType">
                        <option value="">全部类型</option>
                        <option value="1">文字知识点</option>
                        <option value="2">课程知识点</option>
                    </select>
                    <button class="btn btn-primary" @click="doSearch">查询</button>
                    <button class="btn" @click="resetSearch">重置</button>
                    <button class="btn btn-add" @click="$router.push({ name: 'keypoint_add' })">+ 新增知识点</button>
                </div>

                <table>
                    <colgroup>
                        <col style="width:224px"><col style="width:104px"><col style="width:178px"><col style="width:250px">
                        <col style="width:106px"><col style="width:128px"><col style="width:134px"><col style="width:126px">
                    </colgroup>
                    <thead>
                        <tr>
                            <th>知识点</th><th>类型</th><th>关联资源</th><th>摘要</th>
                            <th>页码定位</th><th>视频定位</th><th>创建时间</th><th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in tableData" :key="row.id">
                            <td class="t-kp" :title="getKeyPointDisplay(row)" @click="handleLocate(row)">{{ getKeyPointDisplay(row) || '-' }}</td>
                            <td>
                                <span class="tag" :class="row.keyPointType === 2 ? 'tag-course' : 'tag-text'">
                                    {{ row.keyPointType === 2 ? '课程知识点' : '文字知识点' }}
                                </span>
                            </td>
                            <td class="t-res" :title="getResourceName(row.resourceId)" @click="handlePreviewResource(row)">{{ getResourceName(row.resourceId) }}</td>
                            <td class="t-sum" :title="row.summary">{{ row.summary || '-' }}</td>
                            <td>
                                <span class="anchor page" v-if="pageAnchor(row)">{{ pageAnchor(row) }}</span>
                                <span class="anchor none" v-else>—</span>
                            </td>
                            <td>
                                <span class="anchor video" v-if="videoAnchor(row)">{{ videoAnchor(row) }}</span>
                                <span class="anchor none" v-else>—</span>
                            </td>
                            <td class="t-time">{{ formatTime(row.createTime) }}</td>
                            <td class="op">
                                <span @click="handleLocate(row)">定位</span>
                                <span @click="$router.push({ name: 'keypoint_edit', query: { id: row.id } })">编辑</span>
                                <el-popconfirm
                                    title="是否确定删除？"
                                    popper-class="kp-dark-popper"
                                    @confirm="handleDel(row)"
                                >
                                    <span slot="reference" class="del">删除</span>
                                </el-popconfirm>
                            </td>
                        </tr>
                        <tr v-if="!loading && !tableData.length">
                            <td colspan="8" class="kp-empty">暂无知识点数据</td>
                        </tr>
                    </tbody>
                </table>

                <div class="table-foot">
                    <span>共 {{ total }} 条 · 每页 {{ query.size }} 条 · 当前第 {{ query.page }} 页</span>
                    <el-pagination
                        background
                        layout="prev, pager, next, sizes"
                        :total="total"
                        :page-size="query.size"
                        :page-sizes="pageSizes"
                        :current-page="query.page"
                        @current-change="handlePageChange"
                        @size-change="handleSizeChange"
                    >
                    </el-pagination>
                </div>
            </div>
        </div>

        <!-- 文本预览对话框 -->
        <el-dialog
            title="文本预览"
            :visible.sync="textPreviewVisible"
            width="600px"
            custom-class="kp-dark-dialog">
            <div class="text-preview-content">{{ previewContent }}</div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="textPreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 图片预览 -->
        <el-dialog
            title="图片预览"
            :visible.sync="imagePreviewVisible"
            width="800px"
            custom-class="kp-dark-dialog">
            <div class="image-preview-content" oncontextmenu="return false">
                <img :src="previewUrl" style="max-width: 100%; max-height: 500px;" oncontextmenu="return false" />
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="imagePreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 视频预览 -->
        <el-dialog
            title="视频预览"
            :visible.sync="videoPreviewVisible"
            width="800px"
            custom-class="kp-dark-dialog"
            @close="handleVideoClose">
            <div class="video-preview-content">
                <video
                    ref="previewVideo"
                    :src="previewUrl"
                    controls
                    preload="auto"
                    controlslist="nodownload"
                    oncontextmenu="return false"
                    style="max-width: 100%; max-height: 500px;">
                </video>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="videoPreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 文档预览 -->
        <el-dialog
            title="文档预览"
            :visible.sync="docPreviewVisible"
            width="90%"
            top="5vh"
            custom-class="doc-preview-dialog kp-dark-dialog"
            @close="handleDocPreviewClose">
            <div v-loading="docPreviewLoading" element-loading-text="文档加载中..." element-loading-background="rgba(6,21,48,0.72)" style="height: 75vh;">
                <iframe
                    v-if="previewUrl"
                    ref="docPreviewIframe"
                    :src="previewUrl"
                    style="width: 100%; height: 100%; border: none;"
                    allowfullscreen
                    @load="handleIframeLoad">
                </iframe>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import * as echarts from 'echarts'
import { lists, del } from '@/request/api/keypoint.js'
import { lists as getResourceList, getPreviewUrl } from '@/request/api/source.js'
import { get as getKnowledgeDetail } from '@/request/api/knowledge_graph.js'
import moment from 'moment'
export default {
    data() {
        return {
            tableData: [],
            // 空串不提交，数据范围由后端按角色判定
            query: { keyPoint: '', resourceId: '', keyPointType: '', page: 1, size: 10 },
            pageSizes: [10, 30, 50, 100],
            total: 0,
            loading: false,
            resourceMap: {}, // 资源ID到资源的映射
            resOptions: [], // 资源下拉项
            knowledgeNameMap: {}, // 课程知识点ID到名称的映射
            allKeyPoints: [], // 全量知识点（用于统计）
            stat: {
                total: 0,
                textCount: 0,
                courseCount: 0,
                resCount: 0,
                topResText: '暂无数据',
                pageLoc: 0,
                videoLoc: 0,
                maxSpan: 0,
                typeDist: [],
                resRank: [],
                locDist: []
            },
            pendingSeek: 0, // 「定位」时待跳转的视频秒数
            // 资源类型映射
            typeMap: {
                1: '文本',
                2: '超链接',
                3: 'Word',
                4: 'Excel',
                5: 'PPT',
                6: '图片',
                7: '视频'
            },
            // 预览相关
            textPreviewVisible: false,
            imagePreviewVisible: false,
            videoPreviewVisible: false,
            docPreviewVisible: false,
            previewContent: '',
            previewUrl: '',
            docPreviewLoading: false,
            // IMM 预览凭证
            immAccessToken: '',
            immRefreshToken: ''
        }
    },
    watch: {
        $route(val, old) {
            if (val.name == 'keypoint') {
                this.syncQuery()
                this.init()
            }
        }
    },
    created() {
        this.syncQuery()
        this.loadResourceList()
    },
    mounted() {
        window.addEventListener('resize', this.onResize)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        if (this.typeChart) this.typeChart.dispose()
    },
    methods: {
        // 支持从资源列表带 resourceId 直接过滤
        syncQuery() {
            const rid = this.$route.query.resourceId
            this.query.resourceId = rid ? Number(rid) : ''
            this.query.page = 1
        },
        // 加载资源列表（用于资源名映射 + 下拉项 + 统计）
        loadResourceList() {
            getResourceList({ page: 1, size: 1000 }).then(res => {
                const list = res.data.list || []
                this.resourceMap = {}
                list.forEach(item => {
                    this.resourceMap[item.id] = item
                })
                this.resOptions = list.map(function (item) {
                    return { id: item.id, name: item.name }
                })
                this.loadStats()
                this.init()
            })
        },
        // 全量知识点 → KPI + 图表
        loadStats() {
            lists({ page: 1, size: 1000 }).then(res => {
                const rows = res.data.list || []
                this.allKeyPoints = rows
                this.buildStat(rows)
                this.$nextTick(() => this.renderChart())
            }).catch(() => {})
        },
        buildStat(rows) {
            const resMap = {}
            const typeMap = {}
            let pageLoc = 0, videoLoc = 0, maxSpan = 0
            let onlyPage = 0, onlyVideo = 0, both = 0

            rows.forEach(r => {
                const tn = r.keyPointType === 2 ? '课程知识点' : '文字知识点'
                typeMap[tn] = (typeMap[tn] || 0) + 1

                if (r.resourceId != null) {
                    if (!resMap[r.resourceId]) resMap[r.resourceId] = 0
                    resMap[r.resourceId]++
                }

                const hasPage = r.startPage != null
                const hasVideo = r.videoStartTime != null
                if (hasPage) {
                    pageLoc++
                    const span = (r.endPage != null ? r.endPage : r.startPage) - r.startPage + 1
                    if (span > maxSpan) maxSpan = span
                }
                if (hasVideo) videoLoc++
                if (hasPage && hasVideo) both++
                else if (hasPage) onlyPage++
                else if (hasVideo) onlyVideo++
            })

            const resRank = Object.keys(resMap).map(id => {
                return { id: Number(id), name: this.getResourceName(id), value: resMap[id] }
            }).sort((a, b) => b.value - a.value)
            const max = resRank.length ? resRank[0].value : 1
            const top8 = resRank.slice(0, 8)
            top8.forEach(r => { r.pct = Math.max(Math.round(r.value / max * 100), 6) })

            const locDist = [
                { name: '仅页码定位', desc: '文档类', value: onlyPage, cls: 'b2' },
                { name: '仅视频定位', desc: '音视频类', value: onlyVideo, cls: 'b3' },
                { name: '页码 + 视频双定位', desc: '可交叉跳转', value: both, cls: 'b1' }
            ].filter(l => l.value > 0)
            locDist.forEach(l => {
                l.pct = rows.length ? Math.round(l.value / rows.length * 1000) / 10 : 0
                if (l.pct < 4) l.pct = 4
            })

            this.stat = {
                total: rows.length,
                textCount: typeMap['文字知识点'] || 0,
                courseCount: typeMap['课程知识点'] || 0,
                resCount: resRank.length,
                topResText: resRank.length ? resRank[0].name + ' 最多 ' + resRank[0].value + ' 条' : '暂无数据',
                pageLoc: pageLoc,
                videoLoc: videoLoc,
                maxSpan: maxSpan,
                typeDist: Object.keys(typeMap).map(k => ({ name: k, value: typeMap[k] })),
                resRank: top8,
                locDist: locDist
            }
        },
        onResize() {
            if (this.typeChart) this.typeChart.resize()
        },
        renderChart() {
            const el = this.$refs.typeChart
            if (!el) return
            this.typeChart = echarts.init(el)
            const dist = this.stat.typeDist
            const total = this.stat.total
            const colorOf = name => name === '课程知识点' ? '#a78bfa' : '#4fc3ff'
            this.typeChart.setOption({
                tooltip: {
                    trigger: 'item',
                    confine: true,
                    backgroundColor: 'rgba(6,21,48,.95)',
                    borderColor: 'rgba(63,156,255,.5)',
                    textStyle: { color: '#cfe6ff', fontSize: 12 },
                    formatter: p => p.name + '：' + p.value + ' 条（' + p.percent + '%）'
                },
                legend: {
                    orient: 'vertical',
                    right: 6,
                    top: 'middle',
                    itemWidth: 8,
                    itemHeight: 8,
                    itemGap: 10,
                    icon: 'rect',
                    formatter: name => {
                        const hit = dist.find(d => d.name === name)
                        return name + '  ' + (hit ? hit.value : 0)
                    },
                    textStyle: { color: '#a9c6e8', fontSize: 11 }
                },
                color: dist.map(d => colorOf(d.name)),
                series: [{
                    type: 'pie',
                    radius: ['54%', '76%'],
                    center: ['32%', '52%'],
                    itemStyle: { borderColor: '#061530', borderWidth: 2 },
                    label: { show: false },
                    labelLine: { show: false },
                    data: dist.map(d => ({ name: d.name, value: d.value }))
                }],
                graphic: total ? [
                    {
                        type: 'text', left: '26%', top: '44%',
                        style: { text: String(total), fill: '#eaf6ff', font: '500 20px "DIN Alternate", sans-serif', textAlign: 'center' }
                    },
                    {
                        type: 'text', left: '27%', top: '60%',
                        style: { text: '个知识点', fill: '#7ea6d9', font: '11px sans-serif', textAlign: 'center' }
                    }
                ] : []
            })
        },
        fillCls(i) {
            return ['gold', 'green', 'purple', '', '', '', '', ''][i] || ''
        },
        // 表格分页数据
        init() {
            this.loading = true
            lists(this.buildParams()).then(res => {
                this.tableData = res.data.list || []
                this.total = res.data.total || 0
                // 加载课程知识点名称
                this.loadKnowledgeNames()
            }).finally(() => {
                this.loading = false
            })
        },
        buildParams() {
            const params = { page: this.query.page, size: this.query.size }
            if (this.query.keyPoint) params.keyPoint = this.query.keyPoint
            if (this.query.resourceId !== '') params.resourceId = this.query.resourceId
            if (this.query.keyPointType !== '') params.keyPointType = this.query.keyPointType
            return params
        },
        doSearch() {
            this.query.page = 1
            this.init()
        },
        resetSearch() {
            this.query.keyPoint = ''
            this.query.resourceId = ''
            this.query.keyPointType = ''
            this.query.page = 1
            this.init()
        },
        // 加载课程知识点的名称
        loadKnowledgeNames() {
            var self = this
            var courseRows = this.tableData.filter(function (row) {
                return row.keyPointType === 2 && row.keyPoint && !self.knowledgeNameMap[row.keyPoint]
            })
            courseRows.forEach(function (row) {
                getKnowledgeDetail(row.keyPoint).then(function (res) {
                    if (res.code == 0 && res.data && res.data.name) {
                        self.$set(self.knowledgeNameMap, row.keyPoint, res.data.name)
                    }
                }).catch(function () { })
            })
        },
        // 获取知识点显示文本
        getKeyPointDisplay(row) {
            if (row.keyPointType === 2 && row.keyPoint) {
                return this.knowledgeNameMap[row.keyPoint] || row.keyPoint
            }
            return row.keyPoint || '-'
        },
        // 根据resourceId获取资源名称
        getResourceName(resourceId) {
            const resource = this.resourceMap[resourceId]
            return resource ? resource.name : '-'
        },
        // 页码定位：P1–33
        pageAnchor(row) {
            if (row.startPage == null && row.endPage == null) return ''
            const s = row.startPage == null ? row.endPage : row.startPage
            const e = row.endPage == null ? row.startPage : row.endPage
            return s === e ? 'P' + s : 'P' + s + '–' + e
        },
        // 视频定位：00:02–44:00
        videoAnchor(row) {
            if (row.videoStartTime == null && row.videoEndTime == null) return ''
            const s = this.mmss(row.videoStartTime)
            const e = this.mmss(row.videoEndTime)
            if (!s) return e
            if (!e || s === e) return s
            return s + '–' + e
        },
        mmss(sec) {
            if (sec == null) return ''
            const n = Number(sec)
            if (isNaN(n)) return ''
            const m = Math.floor(n / 60)
            const s = n % 60
            return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s)
        },
        handlePageChange(page) {
            this.query.page = page
            this.init()
        },
        handleSizeChange(size) {
            this.query.size = size
            this.query.page = 1
            this.init()
        },
        handleDel(data) {
            del({ ids: [data.id] }).then(res => {
                if (res.code != 0) return this.$notify.error({ title: '错误', message: res.msg });
                this.loadStats()
                this.init()
                this.$notify({ title: '成功', message: res.msg, type: 'success' });
            })
        },
        formatTime(time) {
            if (!time) return '-'
            return moment(time).format('YYYY-MM-DD HH:mm')
        },
        // 获取资源类型名称
        getTypeName(type) {
            return this.typeMap[type] || '-'
        },
        // 预览资源（关联资源列 / 知识点名点击）
        handlePreviewResource(row) {
            if (!row.resourceId) {
                this.$message.warning('未关联资源')
                return
            }
            // 从缓存中获取资源信息
            const resource = this.resourceMap[row.resourceId]
            if (!resource) {
                this.$message.warning('资源不存在')
                return
            }
            this.pendingSeek = 0
            this.doPreview({
                type: resource.type,
                resourceUri: resource.resourceUri,
                content: resource.content,
                name: resource.name
            })
        },
        // 「定位」：打开资源预览，视频类自动跳到该知识点的起始时间点
        handleLocate(row) {
            if (!row.resourceId) {
                this.handleLocateFallback(row)
                return
            }
            const resource = this.resourceMap[row.resourceId]
            if (!resource) {
                this.handleLocateFallback(row)
                return
            }
            if (!resource.type && !resource.resourceUri) {
                this.handleLocateFallback(row)
                return
            }
            this.pendingSeek = resource.type === 7 ? (row.videoStartTime || 0) : 0
            this.doPreview({
                type: resource.type,
                resourceUri: resource.resourceUri,
                content: resource.content,
                name: resource.name
            })
        },
        // 没有可预览的资源时，退化为提示定位信息
        handleLocateFallback(row) {
            const page = this.pageAnchor(row)
            const video = this.videoAnchor(row)
            if (!page && !video) {
                this.$message.warning('该知识点未设置定位信息')
                return
            }
            this.$message.info('定位信息：' + [page, video].filter(Boolean).join(' · '))
        },
        // 执行预览
        doPreview(resource) {
            const type = resource.type
            const apiBaseURL = window.apiUrl?.baseURL || process.env.VUE_APP_BASE_URL || ''

            switch (type) {
                case 1: // 文本
                    this.previewContent = resource.content || '暂无内容'
                    this.textPreviewVisible = true
                    break
                case 2: // 超链接
                    if (resource.resourceUri) {
                        window.open(resource.resourceUri, '_blank')
                    } else {
                        this.$message.warning('暂无链接地址')
                    }
                    break
                case 3: // Word
                case 4: // Excel
                case 5: // PPT
                    if (resource.resourceUri) {
                        const resourceUrl = resource.resourceUri.startsWith('http') ? resource.resourceUri : (apiBaseURL + resource.resourceUri)
                        this.docPreviewVisible = true
                        this.docPreviewLoading = true
                        this.previewUrl = ''
                        this.immAccessToken = ''
                        this.immRefreshToken = ''

                        getPreviewUrl(resourceUrl).then(data => {
                            const { previewUrl, accessToken, refreshToken } = data

                            if (!previewUrl) {
                                this.$message.error('预览地址为空')
                                this.docPreviewVisible = false
                                return
                            }

                            this.immAccessToken = accessToken
                            this.immRefreshToken = refreshToken

                            let finalUrl = previewUrl
                            if (accessToken) {
                                const separator = previewUrl.includes('?') ? '&' : '?'
                                finalUrl = `${previewUrl}${separator}accessToken=${accessToken}`
                            }

                            this.previewUrl = finalUrl
                        }).catch(error => {
                            // 更详细的错误提示
                            let errorMsg = '获取预览凭证失败'
                            if (error.message) {
                                errorMsg = error.message
                            } else if (typeof error === 'string') {
                                errorMsg = error
                            }
                            this.$message.error(errorMsg)
                            this.docPreviewVisible = false
                        }).finally(() => {
                            this.docPreviewLoading = false
                        })
                    } else {
                        this.$message.warning('暂无文件地址')
                    }
                    break
                case 6: // 图片
                    if (resource.resourceUri) {
                        const imageUrl = resource.resourceUri.startsWith('http') ? resource.resourceUri : (apiBaseURL + resource.resourceUri)
                        this.previewUrl = imageUrl
                        this.imagePreviewVisible = true
                    } else {
                        this.$message.warning('暂无图片地址')
                    }
                    break
                case 7: // 视频
                    if (resource.resourceUri) {
                        const videoUrl = resource.resourceUri.startsWith('http') ? resource.resourceUri : (apiBaseURL + resource.resourceUri)
                        const seek = this.pendingSeek
                        this.previewUrl = ''
                        this.videoPreviewVisible = true
                        // 延迟1秒后设置URL并自动播放
                        setTimeout(() => {
                            this.previewUrl = videoUrl
                            // 等待DOM更新后播放
                            this.$nextTick(() => {
                                const video = this.$refs.previewVideo
                                if (video) {
                                    // 定位时先挂上 seek，等元数据就绪再跳转
                                    video.onloadedmetadata = () => {
                                        if (seek) {
                                            try { video.currentTime = seek } catch (e) { /* 元数据未就绪时忽略 */ }
                                        }
                                    }
                                    video.play().catch(err => {
                                        console.log('自动播放失败:', err)
                                    })
                                }
                            })
                        }, 1000)
                    } else {
                        this.$message.warning('暂无视频地址')
                    }
                    break
                default:
                    this.$message.warning('未知资源类型')
            }
        },
        // 视频对话框关闭时暂停视频
        handleVideoClose() {
            if (this.$refs.previewVideo) {
                this.$refs.previewVideo.pause()
            }
            this.pendingSeek = 0
        },
        // 文档预览对话框关闭
        handleDocPreviewClose() {
            this.previewUrl = ''
            this.immAccessToken = ''
            this.immRefreshToken = ''
            window.removeEventListener('message', this.handleImmMessage)
        },
        // 监听 IMM iframe 消息
        handleImmMessage(event) {
            if (event.data && (event.data.type === 'getAccessToken' || event.data === 'getAccessToken')) {
                if (this.$refs.docPreviewIframe && this.immAccessToken) {
                    this.$refs.docPreviewIframe.contentWindow.postMessage({
                        type: 'setAccessToken',
                        data: {
                            accessToken: this.immAccessToken,
                            refreshToken: this.immRefreshToken
                        }
                    }, '*')
                }
            }
        },
        // iframe 加载完成后传递 token
        handleIframeLoad() {
            window.addEventListener('message', this.handleImmMessage)
            if (this.$refs.docPreviewIframe && this.immAccessToken) {
                setTimeout(() => {
                    this.$refs.docPreviewIframe.contentWindow.postMessage({
                        type: 'setAccessToken',
                        data: {
                            accessToken: this.immAccessToken,
                            refreshToken: this.immRefreshToken
                        }
                    }, '*')
                }, 100)
            }
        }
    }
}
</script>

<style scoped lang="less">
.kp-keypoint {
    .kp-page {
        margin: 0 10px 20px;
        color: #cfe6ff;
        font-size: 13px;
    }

    /* 标题横幅 */
    .kp-banner {
        position: relative;
        text-align: center;
        padding: 14px 0 10px;
        margin-bottom: 16px;

        h1 {
            display: inline-block;
            font-size: 24px;
            font-weight: 500;
            letter-spacing: 6px;
            color: #eaf6ff;
            text-shadow: 0 0 18px rgba(64, 156, 255, 0.9), 0 0 40px rgba(32, 110, 255, 0.5);
            padding: 6px 56px;
            background: linear-gradient(180deg, rgba(24, 80, 190, 0.45), rgba(10, 35, 90, 0.15));
            clip-path: polygon(24px 0, calc(100% - 24px) 0, 100% 100%, 0 100%);
            border-bottom: 1px solid rgba(80, 180, 255, 0.7);
            margin: 0;
        }

        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 26px;
            width: 180px;
            height: 2px;
            background: linear-gradient(90deg, transparent, #3f9cff);
        }

        &::before { left: 6%; }
        &::after { right: 6%; transform: scaleX(-1); }
    }

    /* KPI */
    .kp-kpis {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 14px;
        margin-bottom: 16px;
    }

    .kp-kpi {
        display: flex;
        align-items: center;
        gap: 14px;
        background: linear-gradient(180deg, rgba(16, 55, 130, 0.35), rgba(8, 25, 60, 0.35));
        border: 1px solid rgba(63, 156, 255, 0.35);
        border-radius: 8px;
        padding: 14px 18px;
        position: relative;
        overflow: hidden;

        &::after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background: linear-gradient(180deg, var(--c, #4fc3ff), rgba(27, 98, 224, 0.7));
            box-shadow: 0 0 10px var(--c, #4fc3ff);
        }

        &::before {
            content: '';
            position: absolute;
            right: -30px;
            top: -30px;
            width: 90px;
            height: 90px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(63, 156, 255, 0.18), transparent 70%);
        }

        .kp-ring {
            flex: none;
            width: 52px;
            height: 52px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(30, 90, 200, 0.5), rgba(8, 25, 60, 0.9));
            border: 1px solid rgba(79, 195, 255, 0.55);
            box-shadow: 0 0 14px rgba(63, 156, 255, 0.4), inset 0 0 12px rgba(63, 156, 255, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .info .label {
            font-size: 12px;
            color: #7ea6d9;
            margin-bottom: 4px;
        }

        .info .value {
            font-size: 26px;
            font-weight: 500;
            font-family: 'DIN Alternate', 'Segoe UI', sans-serif;
            background: linear-gradient(180deg, #ffffff, var(--c, #4fc3ff));
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;

            small {
                font-size: 12px;
                margin-left: 3px;
            }
        }

        .info .trend {
            font-size: 11px;
            margin-top: 3px;
            color: #8fb4dc;

            i {
                color: #ffd24a;
                font-style: normal;
            }
        }
    }

    /* 卡片 */
    .kp-card {
        background: linear-gradient(180deg, rgba(13, 40, 95, 0.35), rgba(5, 18, 45, 0.55));
        border: 1px solid rgba(63, 156, 255, 0.28);
        border-radius: 8px;
        padding: 14px 16px;
        box-shadow: inset 0 0 24px rgba(20, 70, 170, 0.15);
        margin-bottom: 16px;
    }

    .kp-grid {
        display: grid;
        grid-template-columns: 4fr 5fr 3fr;
        gap: 14px;
        margin-bottom: 16px;
    }

    .kp-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #dceeff;
        letter-spacing: 1px;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(63, 156, 255, 0.35);

        &::before {
            content: '';
            width: 8px;
            height: 8px;
            background: var(--accent, #3f9cff);
            transform: rotate(45deg);
            box-shadow: 0 0 8px var(--accent, #3f9cff);
        }

        .more {
            margin-left: auto;
            font-size: 12px;
            color: #5f8fd0;
            font-weight: 400;
        }
    }

    .kp-chart {
        height: 208px;
        width: 100%;
    }

    /* 排行榜 */
    .kp-rank {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 2px;
    }

    .rank-row {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 12px;
    }

    .rank-no {
        flex: none;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        font-size: 11px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Consolas, monospace;
        background: rgba(63, 156, 255, 0.14);
        color: #7ea6d9;
        border: 1px solid rgba(63, 156, 255, 0.3);

        &.n1 { background: linear-gradient(180deg, #e0a300, #b8860b); color: #fff7d6; border-color: #ffd24a; box-shadow: 0 0 10px rgba(255, 210, 74, 0.55); }
        &.n2 { background: linear-gradient(180deg, #8fa6c4, #5c7392); color: #f0f6ff; border-color: #a9c6e8; }
        &.n3 { background: linear-gradient(180deg, #b9762f, #8a5220); color: #ffeede; border-color: #d99354; }
    }

    .rank-name {
        flex: none;
        width: 214px;
        color: #dceeff;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .rank-track {
        flex: 1;
        height: 8px;
        border-radius: 4px;
        background: rgba(63, 156, 255, 0.13);
        overflow: hidden;
    }

    .rank-fill {
        height: 100%;
        border-radius: 4px;
        background: linear-gradient(90deg, #1b62e0, #4fc3ff);
        box-shadow: 0 0 8px rgba(79, 195, 255, 0.65);

        &.gold { background: linear-gradient(90deg, #b8860b, #ffd24a); box-shadow: 0 0 8px rgba(255, 210, 74, 0.7); }
        &.purple { background: linear-gradient(90deg, #6b46c1, #a78bfa); box-shadow: 0 0 8px rgba(167, 139, 250, 0.7); }
        &.green { background: linear-gradient(90deg, #0f8f6a, #35e0b0); box-shadow: 0 0 8px rgba(53, 224, 176, 0.7); }
    }

    .rank-val {
        flex: none;
        width: 52px;
        text-align: right;
        font-family: Consolas, monospace;
        font-size: 13px;
        color: #6fc4ff;

        b {
            font-weight: 500;
            font-size: 14px;
        }
    }

    /* 定位方式分布 */
    .loc-list {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin-top: 6px;
    }

    .loc-item {
        .loc-top {
            display: flex;
            align-items: baseline;
            font-size: 12px;
            color: #a9c6e8;
            margin-bottom: 6px;

            b {
                margin-left: auto;
                font-family: Consolas, monospace;
                font-size: 16px;
                color: #dceeff;
            }

            em {
                font-style: normal;
                font-size: 11px;
                color: #5f8fd0;
                margin-left: 6px;
            }
        }
    }

    .loc-bar {
        height: 9px;
        border-radius: 5px;
        background: rgba(63, 156, 255, 0.13);
        overflow: hidden;

        i {
            display: block;
            height: 100%;
            border-radius: 5px;

            &.b1 { background: linear-gradient(90deg, #0f8f6a, #35e0b0); box-shadow: 0 0 8px rgba(53, 224, 176, 0.7); }
            &.b2 { background: linear-gradient(90deg, #1b62e0, #4fc3ff); box-shadow: 0 0 8px rgba(79, 195, 255, 0.7); }
            &.b3 { background: linear-gradient(90deg, #6b46c1, #a78bfa); box-shadow: 0 0 8px rgba(167, 139, 250, 0.7); }
        }
    }

    /* 工具栏 */
    .kp-toolbar {
        display: flex;
        gap: 10px;
        align-items: center;
        margin-bottom: 14px;
        flex-wrap: wrap;
    }

    .search-box {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(13, 40, 95, 0.5);
        border: 1px solid rgba(63, 156, 255, 0.4);
        border-radius: 5px;
        padding: 0 12px;
        height: 34px;
        width: 220px;

        input {
            background: transparent;
            border: none;
            outline: none;
            color: #cfe6ff;
            font-size: 13px;
            width: 100%;

            &::placeholder { color: #4f77ab; }
        }
    }

    select {
        height: 34px;
        padding: 0 10px;
        border-radius: 5px;
        background: rgba(13, 40, 95, 0.5);
        border: 1px solid rgba(63, 156, 255, 0.4);
        color: #a9c6e8;
        font-size: 13px;
        outline: none;
        cursor: pointer;
        max-width: 240px;

        option { background: #0a1f45; color: #a9c6e8; }
    }

    .btn {
        height: 34px;
        padding: 0 16px;
        border-radius: 5px;
        font-size: 13px;
        cursor: pointer;
        border: 1px solid rgba(63, 156, 255, 0.45);
        background: rgba(13, 40, 95, 0.5);
        color: #a9c6e8;
        transition: all 0.2s;

        &:hover { border-color: #4fc3ff; color: #4fc3ff; }
    }

    .btn-primary {
        background: linear-gradient(180deg, #2a7de8, #1b62e0);
        border: 1px solid #4fc3ff;
        color: #fff;
        box-shadow: 0 0 12px rgba(42, 125, 232, 0.6);

        &:hover { filter: brightness(1.15); color: #fff; }
    }

    .btn-add {
        margin-left: auto;
        background: linear-gradient(180deg, #22b98a, #0f8f6a);
        border: 1px solid #35e0b0;
        color: #eafff7;
        box-shadow: 0 0 12px rgba(53, 224, 176, 0.55);

        &:hover { filter: brightness(1.12); color: #fff; }
    }

    /* 表格 */
    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }

    thead th {
        background: linear-gradient(180deg, rgba(30, 85, 190, 0.5), rgba(12, 38, 90, 0.5));
        color: #bfe0ff;
        font-size: 12px;
        font-weight: 500;
        padding: 12px 14px;
        text-align: left;
        letter-spacing: 1px;
        border-bottom: 1px solid rgba(79, 195, 255, 0.6);
        box-shadow: 0 2px 10px rgba(27, 98, 224, 0.25);
        white-space: nowrap;
    }

    tbody td {
        padding: 12px 14px;
        font-size: 13px;
        color: #c4dcf5;
        border-bottom: 1px solid rgba(63, 156, 255, 0.12);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    tbody tr:nth-child(even) { background: rgba(20, 60, 140, 0.12); }
    tbody tr { transition: background 0.15s; }
    tbody tr:hover { background: rgba(63, 156, 255, 0.1); }
    tbody tr:hover td:first-child { box-shadow: inset 3px 0 0 #4fc3ff; }

    .t-kp {
        color: #eaf6ff;
        cursor: pointer;

        &:hover { color: #4fc3ff; text-shadow: 0 0 8px rgba(79, 195, 255, 0.6); }
    }

    .t-sum { color: #93b6de; font-size: 12px; }

    .t-res {
        color: #9ec7ff;
        font-size: 12px;
        cursor: pointer;

        &:hover { color: #4fc3ff; }
    }

    .t-time { font-family: Consolas, monospace; font-size: 12px; color: #7ea6d9; }

    /* 标签 */
    .tag {
        display: inline-block;
        font-size: 12px;
        padding: 2px 10px;
        border-radius: 3px;
        white-space: nowrap;

        &.tag-text { color: #6fc4ff; border: 1px solid rgba(79, 195, 255, 0.45); background: rgba(27, 98, 224, 0.2); }
        &.tag-course { color: #a78bfa; border: 1px solid rgba(167, 139, 250, 0.45); background: rgba(167, 139, 250, 0.12); }
    }

    /* 定位胶囊 */
    .anchor {
        display: inline-block;
        font-family: Consolas, monospace;
        font-size: 11.5px;
        padding: 2px 9px;
        border-radius: 10px;
        white-space: nowrap;

        &.page { color: #35e0b0; background: rgba(53, 224, 176, 0.1); border: 1px solid rgba(53, 224, 176, 0.4); }
        &.video { color: #a78bfa; background: rgba(167, 139, 250, 0.1); border: 1px solid rgba(167, 139, 250, 0.42); }
        &.none { color: #4f77ab; background: rgba(63, 156, 255, 0.06); border: 1px solid rgba(63, 156, 255, 0.2); }
    }

    .op {
        white-space: nowrap;

        span {
            cursor: pointer;
            font-size: 12px;
            margin-right: 10px;
            color: #6fc4ff;

            &:hover { color: #4fc3ff; text-shadow: 0 0 8px rgba(79, 195, 255, 0.6); }
        }

        .del {
            color: #ff6b6b;
            margin-right: 0;

            &:hover { color: #ff8a8a; text-shadow: 0 0 8px rgba(255, 107, 107, 0.6); }
        }
    }

    .table-foot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 13px 0 0;
        font-size: 12px;
        color: #7ea6d9;
    }

    .kp-empty {
        padding: 24px 0;
        text-align: center;
        color: #5f8fd0;
        font-size: 12px;
    }

    ::v-deep .el-pagination {
        padding: 0;

        button,
        .el-pager li {
            background: rgba(13, 40, 95, 0.5) !important;
            color: #a9c6e8 !important;
            border: 1px solid rgba(63, 156, 255, 0.4);
            border-radius: 4px;
            min-width: 28px;
            height: 28px;
            line-height: 26px;
            font-size: 12px;
            margin: 0 3px;
        }

        button:disabled { opacity: 0.4; }

        .el-pager li.active {
            background: linear-gradient(180deg, #2a7de8, #1b62e0) !important;
            border-color: #4fc3ff;
            color: #fff !important;
            box-shadow: 0 0 10px rgba(79, 195, 255, 0.6);
        }

        .el-pager li:hover:not(.active) {
            border-color: #4fc3ff;
            color: #4fc3ff !important;
        }

        .el-pagination__sizes .el-input .el-input__inner {
            background: rgba(13, 40, 95, 0.5) !important;
            border-color: rgba(63, 156, 255, 0.4) !important;
            color: #a9c6e8 !important;
            height: 28px;
            line-height: 28px;
        }
    }

    .text-preview-content {
        max-height: 400px;
        overflow-y: auto;
        padding: 15px;
        background-color: rgba(6, 21, 48, 0.6);
        border: 1px solid rgba(63, 156, 255, 0.25);
        border-radius: 4px;
        color: #cfe6ff;
        white-space: pre-wrap;
        word-break: break-all;
        line-height: 1.6;
    }

    .image-preview-content,
    .video-preview-content {
        text-align: center;
    }

    @media (max-width: 1500px) {
        .kp-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .kp-grid { grid-template-columns: 1fr 1fr; }
    }
}
</style>

<style lang="less">
/* 弹层 / 弹窗挂在 body 下，需全局样式 */
.kp-dark-dialog {
    background: linear-gradient(180deg, rgba(10, 32, 75, 0.98), rgba(5, 18, 45, 0.98)) !important;
    border: 1px solid rgba(63, 156, 255, 0.45) !important;
    box-shadow: 0 20px 60px rgba(2, 10, 28, 0.8);

    .el-dialog__header {
        border-bottom: 1px solid rgba(63, 156, 255, 0.2);
    }

    .el-dialog__title {
        color: #eaf6ff;
        font-size: 15px;
        letter-spacing: 1px;
    }

    .el-dialog__headerbtn .el-icon-close {
        color: #7ea6d9;
    }

    .el-dialog__body {
        color: #cfe6ff;
    }

    .el-button {
        background: rgba(13, 40, 95, 0.6);
        border-color: rgba(63, 156, 255, 0.45);
        color: #a9c6e8;
    }

    .el-button:hover {
        border-color: #4fc3ff;
        color: #4fc3ff;
    }
}

.kp-dark-popper {
    background: rgba(10, 32, 75, 0.96) !important;
    border: 1px solid rgba(63, 156, 255, 0.45) !important;
    color: #cfe6ff !important;

    .el-popconfirm__main {
        color: #cfe6ff !important;
    }

    .el-popconfirm__action .el-button--text {
        color: #6fc4ff !important;
    }

    .popper__arrow::after {
        border-top-color: rgba(10, 32, 75, 0.96) !important;
    }
}
</style>
